import React, {useEffect, useRef, useState} from 'react';
import Grid from '@material-ui/core/Grid'
import MyProfileTagManagementStyled from './styled'
import {Box, LinearProgress} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import RegularButton from '../../atoms/regular-button';
import MaterialTable from 'material-table';
import { PencilLine, Trash2 } from 'lucide-react'
import {MTText, SearchInput, ActionsCell, ActionsGroup} from '../../atoms'
import { tokens } from '../../../assets/tokens'
import {MODULES, QUERY_KEYS} from '../../../config/constants';
import _, {isArray, isObject} from 'lodash';
import {useTags} from "../../../api/queries/useTags";
import ModalCreateTag from "../../molecules/modal-create-tag";
import ModalDeleteTag from "../../molecules/modal-delete-tag";
import {isTagInUse} from "../../../api";
import {useAppContext} from "../../../context/appContext";
import {invalidateQueriesByKeysWithNoModule, removeQueriesByKeys} from "../../../api/mutations/mutationCallbacks";

const MyProfileTagManagement = ({ i18n, classes, self_data }) => {
    const {module} = useAppContext();

    const [searchTerm, setSearchTerm] = useState({ term: '' });
    const [moduleFilter, setModuleFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [editingTagData, setEditingTagData] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [moduleOptions, setModuleOptions] = useState([]);
    const {data: tags, isLoading} = useTags();
    const [clientHasModules, setClientHasModules] = useState([]);
    const [deleteParams, setDeleteParams] = useState(null);
    const lastDeleteTagId = useRef(null);

    useEffect(() => {
        if (self_data) {
            let hasModules = [];
            if (isObject(self_data.technical_selection_user) && self_data.roles.includes(`ROLE_ATS_ADMIN_${MODULES.ID_TALENT_ACQUISITION}`)) {
                hasModules.push(MODULES.ID_TALENT_ACQUISITION);
            }
            if (isObject(self_data.user_on_boarding) && self_data.roles.includes(`ROLE_ATS_ADMIN_${MODULES.ID_ON_BOARDING}`)) {
                hasModules.push(MODULES.ID_ON_BOARDING);
            }
            if (isObject(self_data.user_feedback) && self_data.roles.includes(`ROLE_ATS_ADMIN_${MODULES.ID_FEEDBACK}`)) {
                hasModules.push(MODULES.ID_FEEDBACK);
            }
            if (isObject(self_data.user_exit_interview) && self_data.roles.includes(`ROLE_ATS_ADMIN_${MODULES.ID_EXIT_INTERVIEW}`)) {
                hasModules.push(MODULES.ID_EXIT_INTERVIEW);
            }
            if (hasModules.length > 0) setClientHasModules(hasModules);
        }
        // eslint-disable-next-line
    }, [tags]);

    useEffect(() => {
        if (clientHasModules && clientHasModules.length >= 1) {
            setModuleOptions(clientHasModules.map(module => ({id: module, name: i18n.module_names[module]})));
        }
    }, [clientHasModules]);

    if (isLoading) {
        return <LinearProgress />
    }

    const handleDataUpdate = async () => {
        try {
            await invalidateQueriesByKeysWithNoModule([`${QUERY_KEYS.TAGS}`]);
            await removeQueriesByKeys([`${module}:${QUERY_KEYS.TAGS_TYPES}`, `${module}:${QUERY_KEYS.INBOX}`]);

            if (isEdit || isDelete) {
                setIsEdit(false);
                setIsDelete(false);
            }
        } catch (error) {
            console.error("Error updating data:", error);
        }
    };

    const filterData = (searchTermValue, moduleFilterValue, tagsList) => {
        if (!tagsList || tagsList.length === 0) {
            return [];
        }

        const filteredData = tagsList.filter((tag) => {
            const nameMatch = tag.name && tag.name.toLowerCase().includes(searchTermValue.toLowerCase());
            const descriptionMatch = tag.description && tag.description.toLowerCase().includes(searchTermValue.toLowerCase());
            const moduleMatch = tag.module && tag.module.toLowerCase().includes(searchTermValue.toLowerCase());

            return nameMatch || descriptionMatch || moduleMatch;
        });

        const moduleFilteredData = moduleFilterValue
            ? filteredData.filter((tag) => {
                const selectedModule = moduleOptions.find(option => option.id === tag.module);
                const moduleName = selectedModule ? selectedModule.id : tag.module;
                return moduleName === moduleFilterValue;
            })
            : filteredData;

        return moduleFilteredData;
    };

    const filteredTags = filterData(searchTerm.term, moduleFilter, tags);

    const handleSearchTerm = _.debounce((value) => {
        setSearchTerm({ term: value });
    }, 250);

    const handleModuleFilter = (_, value) => {
        setModuleFilter(value ? value.id : '');
    };

    const tableOptions = {
        emptyRowsWhenPaging: false,
        loadingType: 'linear',
        cellStyle: { textAlign: 'center' },
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
        toolbar: false,
        draggable: false,
        selection: false,
    }

    let tableColumns = {
        name: {
            title: i18n.tag_management.name,
            render: function NameColumn(row) {
                return <MTText value={row.name} style={{ fontWeight: '400' }} />;
            },
            field: 'name'
        },
        description: {
            title: i18n.tag_management.description,
            render: function DescriptionColumn(row) {
                return <MTText value={row.description} style={{ fontWeight: '400' }} />;
            },
            field: 'description'
        },
        color: {
            title: i18n.tag_management.color,
            render: function DescriptionColumn(row) {
                return <div className="color-picker-button" style={{ backgroundColor: row.color }}/>
            },
            field: 'color'
        },
    };
    
    const moduleColumn = {
        module: {
            title: i18n.tag_management.module,
            render: function ModuleColumn(row) {
                return <MTText value={i18n.module_names[row.module]} style={{ fontWeight: '400' }} />;
            },
            field: 'module'
        }
    }
    
    const actionColumns = {
        actions: {
            title: i18n.user_management.actions,
            sorting: false,
            headerStyle: { width: 96, whiteSpace: 'nowrap' },
            cellStyle: { width: 96 },
            render: function ActionsColumn(row) {
                return (
                    <ActionsGroup>
                        <ActionsCell
                            icon={<PencilLine size={18} color={tokens.color.text.secondary} />}
                            tooltip={i18n.tag_management.modalTitleUpdate}
                            onClick={() => handleEditIconClick(row)}
                        />
                        <ActionsCell
                            icon={<Trash2 size={18} color={tokens.color.error.main} />}
                            tooltip={i18n.tag_management.removeTag}
                            onClick={() => handleDeleteIconClick(row)}
                        />
                    </ActionsGroup>
                );
            },
            field: 'actions',
            hidden: false,
        }
    }

    if (moduleOptions && moduleOptions.length > 0 && moduleColumn) {
        tableColumns = {...tableColumns, ...moduleColumn};
    }
    tableColumns = {...tableColumns, ...actionColumns};

    const handleEditIconClick = (row) => {
        setEditingTagData(row);
        setIsCreate(false);
        setIsModalOpen(true);
    };

    const handleCreateTag = () => {
        setEditingTagData(null);
        setIsCreate(true);
        setIsModalOpen(true);
    };

    // El modal se abre al momento y comprueba dentro si la etiqueta está en uso (la llamada puede
    // tardar); si en medio se abre otra etiqueta, la respuesta antigua se descarta
    const handleDeleteIconClick = async (row) => {
        setEditingTagData(row);
        setIsCreate(true);
        setIsDelete(true);
        lastDeleteTagId.current = row.id;
        setDeleteParams({currentTag: row, isChecking: true});
        setIsModalDeleteOpen(true);
        if (!row.id) return;
        try {
            let res = await isTagInUse(row.id)
            if (lastDeleteTagId.current !== row.id) return;
            let params = {currentTag: row};
            if (isArray(res) && res.length > 0) {
                params.ids = res;
                params.availableTags = _.filter(tags, (tag) => {
                    return tag.module === row.module && tag.id !== row.id;
                });
            }
            setDeleteParams(params);
        } catch (error) {
            if (lastDeleteTagId.current !== row.id) return;
            setDeleteParams({currentTag: row, checkError: true});
        }
    };

    const columnsArray = Object.values(tableColumns);

    columnsArray.forEach(column => {
        if (column.render) {
            column.render.displayName = `${column.field}_render`;
        }
    });

    // Sin el Paper por defecto de material-table: las tablas van sobre el fondo de la página,
    // como en la bandeja de candidatos
    const tableComponents = { Container: props => <div {...props} /> }

    return (
        <div className={classes.root}>
            <MyProfileTagManagementStyled>
                <div className="containerHeader">
                    <Grid container spacing={4}>
                        <Grid item className="sectionSecond" xs={12} sm={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: "flex-start", gridGap: '15px', maxWidth: "800px", }}>
                                <h2>{i18n.tag_management.titleSection}</h2>
                                <h5>{i18n.tag_management.descriptionSection}</h5>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: "flex-start", placeSelf: 'flex-start' }}>
                                <RegularButton className="button-custom" title={i18n.tag_management.createTag}
                                    onClick={handleCreateTag}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={12} >
                            <Grid item className="headerTable tags-header">
                                <div className="tags-filters">
                                    <SearchInput
                                        placeholder={i18n.evaluativeTests.placeholderInputTerm}
                                        onChange={value => handleSearchTerm(value)}
                                    />
                                    {moduleOptions && moduleOptions.length > 1 && (
                                        <Autocomplete
                                            className='autocomplete-custom'
                                            options={moduleOptions}
                                            getOptionLabel={(option) => option.name}
                                            onChange={handleModuleFilter}
                                            renderInput={(params) => (
                                                <TextField {...params} placeholder={i18n.tag_management.placeholderFilter} variant="outlined"/>
                                            )}
                                        />
                                    )}
                                </div>
                            </Grid>
                            <MaterialTable
                                data={filteredTags ?? tags}
                                columns={columnsArray}
                                options={tableOptions}
                                components={tableComponents}
                            />
                        </Grid>
                        <ModalCreateTag
                            open={isModalOpen}
                            handleClose={() => setIsModalOpen(false)}
                            i18n={i18n}
                            isCreate={isCreate}
                            editingTag={editingTagData}
                            onDataUpdate={handleDataUpdate}
                            clientHasModules={clientHasModules}
                            moduleOptions={moduleOptions}
                        />
                        <ModalDeleteTag
                            open={isModalDeleteOpen}
                            handleClose={() => setIsModalDeleteOpen(false)}
                            i18n={i18n}
                            tagId={editingTagData ? editingTagData.id : ""}
                            tagName={editingTagData ? editingTagData.name : ""}
                            handleOnClickDelete={handleDataUpdate}
                            deleteParams={deleteParams}
                        />
                    </Grid>
                </div>
            </MyProfileTagManagementStyled>
        </div>
    );
};

export default MyProfileTagManagement;
